import { Component, OnInit, ViewChild, TemplateRef, ElementRef, OnDestroy } from '@angular/core';
import { TitleService } from 'src/app/shared/services/title.service';
import { SlideOutComponent } from 'src/app/shared/components/slide-out/slide-out.component';
import { ManualUsageEventService, KeyValueService } from 'src/app/shared/api/services';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { ManualUsageEvent, BulkUploadsState, IClientBillingOption } from 'src/app/shared/api/models';
import { map, timeout } from 'rxjs/operators';
import { LtbDataService } from '../ltb.data.service';
import { matchClientPartyRow } from '../../shared/classes/helpers';

@Component({
  selector: 'e2cb-manual-usage-landing',
  templateUrl: './manual-usage-landing.component.html',
  styleUrls: ['./manual-usage-landing.component.scss']
})
export class ManualUsageLandingComponent implements OnInit, OnDestroy {

// #region Public Variables
matchClientPartyRow = matchClientPartyRow;
showSpinner: boolean;
selectedRow: any;
hideShow: boolean;
@ViewChild('template_file_upload', { static: true }) templateFileUpload;
@ViewChild('deleteTpl', { static: true }) deleteTpl: TemplateRef<ElementRef>;
@ViewChild(SlideOutComponent, { static: true }) slideOutForm: SlideOutComponent;
public filter_clientId: string = null;
public selectedClient = null;
columns: any[];
public rows: ManualUsageEvent[] = [];
public tab1Rows: ManualUsageEvent[] = [];
public tab2Rows: ManualUsageEvent[] = [];
public isCurrentTab: boolean = true;
public isGridLoading: boolean;
public filters: any[] = [];
public currentPeriod: Date;
public nextPeriod: Date;
public showBulkStatus: boolean = false;
public bulkStates: BulkUploadsState[] = [];
public bulkStateDeleteActive = false;
private statusCheckInterval: any;

// #endregion

// #region ReadOnly Variables
  rowHeight = 50;
  selected = [];
// #endregion

  constructor(private _manualUsageEventService: ManualUsageEventService,
              private _titleService: TitleService,
              public _sharedDataService: SharedDataService,
              private _ltbDataService: LtbDataService,
              private _keyValueService: KeyValueService)  {
    this._titleService.Subtitle = 'Manual Usage Home';
    // set billing periods
    this.currentPeriod = new Date(); // Months are index based: 0 - 11
    this.currentPeriod.setMonth(this.currentPeriod.getMonth() - 1); // current period is the prev month
    this.nextPeriod = new Date(); // the next period is the current month.
  }

  ngOnInit() {
    // so other objects/compoents can have a reference to it.
    this._ltbDataService.currenntSlideOutShown = this.slideOutForm;

    this.columns = [
      { name: 'Client Id', prop: 'aviClientId'},
      { name: 'Product', prop: 'product'},
      { name: 'Description', prop: 'description'},
      { name: 'Site Name', prop: 'siteName'},
      { name: 'Amount', prop: 'value'},
      { name: 'Action(s)', prop: 'manualUsageEventId', cellTemplate: this.deleteTpl }
    ];

    // ngx-datatable has "onPush" change detect strategy. So we need to watch for whenever the data changes, and reapply the data/rows.
    this._sharedDataService.updatedManualUsage.subscribe({next: () => {
      this.rows = this._sharedDataService.manualUsage;
      this.isGridLoading = false;
    }});

    this.getLatestUploadStatus();
    this.statusCheckInterval = window.setInterval(() => {
      this.getLatestUploadStatus();
    }, 5000);
  }

  ngOnDestroy(): void {
    window.clearInterval(this.statusCheckInterval);
  }

  public async getLatestUploadStatus() {
    let res = await this._keyValueService.GetClientBillingOptions().toPromise();
    let bulkUploadOptions = res.value.find(v => (v as any).name === "bulkUploadsOptions") as any;
    this.bulkStates = bulkUploadOptions.value as BulkUploadsState[];
  }

  public async loadDataGrids() {
    if (this.filter_clientId === null) { alert("Please select a Client ID"); return; }

    let datePointer = this.currentPeriod;

    try {
      if (!this.isCurrentTab) {
        datePointer = this.nextPeriod;
      }
      this.isGridLoading = true;
      document.querySelectorAll('datatable-body')[0].scrollTop = 1; // fix from bug https://github.com/swimlane/ngx-datatable/issues/1220
      await new Promise((resolve) => { setTimeout(() => resolve(), 100); }); // a render-wait sleep for scroll change (part of bug fix).
      this.rows = [...[]];
      await this._sharedDataService.PopulateManualUsageByPeriod((datePointer.getMonth() + 1), datePointer.getFullYear(), this.filter_clientId);
      // The listening on updateManualUsage in constructor will set the row data.
    } catch (e) {
      this._sharedDataService.HandleError(e);
    }
  }

  public async onTabClick(event) {
    const tabname = event.currentTarget.id;
    if(tabname === "current-tab")
    {
      this.isCurrentTab = true;
      this.loadDataGrids();
    }
    if(tabname === "next-tab")
    {
      this.isCurrentTab = false;
      this.loadDataGrids();
    }
  }

  public async onRemoveRow(id: any) {
    this.isGridLoading = true;
    var response: ManualUsageEvent[];
    var request: ManualUsageEventService.DeleteManualUsageEventParams;
    try
    {
      request = {id: id};
      response = await this._manualUsageEventService.DeleteManualUsageEvent(request).toPromise();
    }
    catch(err)
    {
      console.log(err);
    }
    finally
    {
      this.isGridLoading = false;
      this.loadDataGrids();
    }
  }

  public getState(item: BulkUploadsState) {
    return {
      isError: item.endDateTime < item.startDateTime,
      isFinished: item.endDateTime != null
    };
  }

  public async deleteState(item: BulkUploadsState) {
    this.bulkStateDeleteActive = true;
    let res = await this._keyValueService.DeleteBulkUploadState({state: item}).toPromise();
    this.getLatestUploadStatus();
    this.bulkStateDeleteActive = false;
  }

  onRowSelect(event) {
    // only listen to click events.
    if (event.type !== 'click')
    {
      return;
    }
    this.selectedRow = this.selected[0];
    if(event.type === 'click' && event.column.name !== 'Action(s)')
    {
      this.slideOutForm.show();
    }
  }

  // on the slideout close, remove the selectedRow so reclicks of the same selected element refreshes the data correctly
  onClose($event) {
    this.selectedRow = null;
  }

// --------------------------------------------------

  onTemplateDownloadClick(): void {
  this.showSpinner = true;
  this._ltbDataService.GetLtbTemplateFile()
  .pipe(map((res) => {
    return {
      filename: 'manual_usage_template.xlsx',
      data: new Blob(
        [res['body']],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
      ),
    };
  }))
  .subscribe(res => {
    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveBlob(res.data, res.filename);
    } else {
      const link = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = link;
      a.download = res.filename;
      a.click();
      window.URL.revokeObjectURL(link);
      a.remove();
    }
  }, error => {
    this._sharedDataService.HandleError(error);
    this.showSpinner = false;
  }, () => {
    this.showSpinner = false;
  });
  }

// --------------------------------------------------

  onAccrualDownloadClick(): void {
    this.showSpinner = true;
    this._ltbDataService.GetLtbAccrualFile()
    .pipe(map((res) => {
      return {
        filename: 'manual_usage_accrual.xlsx',
        data: new Blob(
          [res['body']],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
        ),
      };
    }))
    .subscribe(res => {
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(res.data, res.filename);
      } else {
        const link = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = link;
        a.download = res.filename;
        a.click();
        window.URL.revokeObjectURL(link);
        a.remove();
      }
    }, error => {
      this._sharedDataService.HandleError(error);
      this.showSpinner = false;
    }, () => {
      this.showSpinner = false;
    });
  }

// --------------------------------------------------

  onTemplateUploadClick(): void {
    this.templateFileUpload.nativeElement.click();
  }

// --------------------------------------------------

  onTemplateFileUploadChanged(): void {
      this.showSpinner = true;
      const files = this.templateFileUpload.nativeElement.files;
      if (files.length === 1) {
      const formData: FormData = new FormData();
      formData.append('file', files[0], files[0].name);

      this._ltbDataService.PostLtbTemplateFile(formData)
        .subscribe(res => {
          if (res) {
            alert('File posted successfully. Keep track with Bulk Usage Upload Status.');
          } else {
            this._sharedDataService.HandleError(res.errors);
            this.showSpinner = false;
          }
        }, error => {
          this._sharedDataService.HandleError(error);
          this.showSpinner = false;
        }, () => {
          this.showSpinner = false;
          this.templateFileUpload.nativeElement.value = '';
          this.getLatestUploadStatus();
        });
      }
  }

// --------------------------------------------------

  onDataTableFiltersClick() {
    this.loadDataGrids();
  }
}
